import { Card, Inline, Stack, Text, textVariants } from '@etg/wings';
import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { CarrierLogo } from '@eti/components';
import { constants } from '@eti/utils';
import TextWithCopyButton from '../../common/TextWithCopyButton';
import type { BookingNumberForVerification } from './models';

const carrierLogoStyles = css`
  height: 28px;
  width: 28px;
`;

const flightItemStyles = css`
  padding: 8px 0;
`;

const flightNumElementStyles = css`
  font-weight: 700;
`;

const virtualCardDescriptionStyles = css`
  padding-bottom: 8px;
`;

const disclaimerStyles = css`
  color: #737373;
`;

interface BookingVerificationProps {
  bookingNumber: string;
  bookingNumberDetails: BookingNumberForVerification;
}
const BookingVerification = ({ bookingNumber, bookingNumberDetails }: BookingVerificationProps) => {
  const { t } = useTranslation();

  return (
    <Card data-testid={`${bookingNumber}-card`}>
      <Stack spacing={16}>
        {/* Airline reference */}
        <Inline alignY="center" spacing={8}>
          <Text>{t('OrderDetails.CarrierVerification.Drawer.Flight.AirlineBookingReference')}</Text>
          <Inline alignY="center" spacing={4}>
            <TextWithCopyButton
              data-testid="booking-number"
              shouldApplyMouseflowMask
              snowplowInfo={{ category: 'Order Details', label: 'COPY_AIRLINE_REF' }}
              text={bookingNumber}
            />
          </Inline>
        </Inline>

        {/* Flights */}
        <Stack spacing={8}>
          <Text>{t('OrderDetails.CarrierVerification.Drawer.Flight.FlightList.Title')}</Text>
          {bookingNumberDetails.flights.map((flight) => (
            <Inline
              alignY="center"
              className={flightItemStyles}
              key={flight.flightNumber}
              spacing={16}
            >
              <Stack spacing={0}>
                <Inline className={flightNumElementStyles} spacing={4}>
                  <Text data-testid={`${flight.flightNumber}-carrier`}>
                    {bookingNumberDetails.carrierBookingNumber?.carrierName}
                  </Text>
                  <Text data-testid={`${flight.flightNumber}-flightNumber`}>
                    {flight.flightNumber}
                  </Text>
                </Inline>
                <Inline spacing={0}>
                  <Inline spacing={4}>
                    <Text data-testid={`${flight.flightNumber}-origin-cityName`}>
                      {flight.origin.cityName}
                    </Text>
                    <Text
                      data-testid={`${flight.flightNumber}-origin-code`}
                    >{`(${flight.origin.code})`}</Text>
                  </Inline>
                  <div>&nbsp;-&nbsp;</div>
                  <Inline spacing={4}>
                    <Text data-testid={`${flight.flightNumber}-destination-cityName`}>
                      {flight.destination.cityName}
                    </Text>
                    <Text
                      data-testid={`${flight.flightNumber}-destination-code`}
                    >{`(${flight.destination.code})`}</Text>
                  </Inline>
                </Inline>
              </Stack>
              <CarrierLogo
                className={carrierLogoStyles}
                data-testid="carrier-logo"
                id={bookingNumberDetails.carrierBookingNumber.carrierCode || ''}
                imageAlt={bookingNumberDetails.carrierBookingNumber.carrierName || ''}
              />
            </Inline>
          ))}
        </Stack>

        {bookingNumberDetails.virtualCard && (
          <>
            {/* Virtual Card */}
            <Stack className={constants.MOUSE_FLOW_CLASS_NAMES} spacing={0}>
              <Text className={virtualCardDescriptionStyles}>
                {t('OrderDetails.CarrierVerification.Drawer.Flight.VirtualCard.Description')}
              </Text>
              <Text asHtml>
                {t(
                  'OrderDetails.CarrierVerification.Drawer.Flight.VirtualCard.CardNumber',
                  '**** **** **** '.concat(bookingNumberDetails.virtualCard.lastFourDigits),
                )}
              </Text>
              <Text asHtml>
                {t(
                  'OrderDetails.CarrierVerification.Drawer.Flight.VirtualCard.ExpirationDate',
                  bookingNumberDetails.virtualCard.expMonthYear,
                )}
              </Text>
              <Text asHtml>
                {t(
                  'OrderDetails.CarrierVerification.Drawer.Flight.VirtualCard.CVC',
                  bookingNumberDetails.virtualCard.cvc,
                )}
              </Text>
            </Stack>

            {/*  Disclaimer */}
            <Text className={disclaimerStyles} variant={textVariants._12}>
              {t('OrderDetails.CarrierVerification.Drawer.Flight.Disclaimer')}
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default BookingVerification;
